const changeSshPortPost = {
  slug: "change-ssh-port",
  title: "How to Change SSH Port Safely - Complete Security Guide 2026",
  metaTitle: "How to Change SSH Port - Step-by-Step Guide for Linux & Windows",
  metaDescription: "Learn how to change SSH port to secure your server. Complete guide for Linux, Ubuntu, CentOS with examples. Includes firewall rules, best practices, and troubleshooting tips.",
  keywords: [
    "change ssh port",
    "how to change ssh port",
    "ssh security",
    "linux server hardening",
    "ubuntu ssh port",
    "centos ssh configuration",
    "vps security guide",
    "clubhosty vps",
  ],
  excerpt:
    "Learn how to change your SSH port on Linux safely. This practical 2026 guide covers Ubuntu, CentOS, firewall setup, testing steps, and security best practices.",
  category: "Server Security",
  date: "April 1, 2026",
  readTime: "8 min read",
  language: "en-IN",
  clubhostyLinks: [
    {
      label: "ClubHosty VPS Hosting",
      url: "https://www.clubhosty.com/affordable-vps-hosting",
    },
    {
      label: "ClubHosty Cloud Hosting",
      url: "https://www.clubhosty.com/cloud-based-hosting-services",
    },
    {
      label: "ClubHosty Contact Support",
      url: "https://www.clubhosty.com/contact",
    },
  ],
  faqs: [
    {
      question: "Is changing SSH port enough to secure my server?",
      answer:
        "It helps reduce automated attacks, but you should also use SSH keys, disable password login, and set firewall restrictions.",
    },
    {
      question: "Which SSH port should I choose?",
      answer:
        "Use a non-standard port above 1024, ideally above 10000, and ensure it is allowed in your firewall.",
    },
    {
      question: "Can I get locked out after changing SSH port?",
      answer:
        "Yes, if firewall rules are not updated correctly. Keep your current SSH session open until new port login is verified.",
    },
  ],
  relatedPosts: [
    {
      slug: "how-to-install-cpanel-in-vps",
      title: "How to Install cPanel in VPS (Step-by-Step 2026 Guide)",
    },
    {
      slug: "shared-vs-vps-vs-cloud-hosting-2026",
      title: "Shared vs VPS vs Cloud Hosting - Which is Best?",
    },
    {
      slug: "what-is-openclaw-self-hosted-ai-agent-guide",
      title: "What Is OpenClaw? Self-Hosted AI Agent Guide",
    },
  ],
  content: [
    {
      heading: "How to Change SSH Port Safely in 2026 🔐",
      headingLevel: "h1",
      paragraphs: [
        "Changing your SSH port is one of the most effective ways to protect your Linux server from automated attacks and brute-force attempts. In this 2026 guide, we'll show you exactly how to change SSH port safely and securely across different Linux distributions.",
        "SSH (Secure Shell) runs on port 22 by default, making it a prime target for hackers. By changing it to a non-standard port, you can significantly reduce unauthorized access attempts to your server.",
      ],
    },
    {
      heading: "Why Change Your SSH Port? 🛡️",
      headingLevel: "h2",
      paragraphs: [
        "The default SSH port (22) is well-known to attackers and bots that constantly scan the internet for vulnerable servers. By changing it, you add an extra layer of security.",
      ],
      list: [
        "✅ Reduces automated brute-force attacks targeting port 22",
        "✅ Eliminates noise from port scanners looking for SSH services",
        "✅ Adds a useful extra security layer (with keys + firewall)",
        "✅ Helps prevent common exploit attempts on default SSH setups",
        "✅ Reduces server resource usage from repeated failed attempts",
        "✅ Keeps logs cleaner and easier to audit",
      ],
    },
    {
      heading: "Prerequisites 🧰",
      headingLevel: "h2",
      paragraphs: [
        "Before changing SSH port, ensure you have:",
      ],
      list: [
        "🔑 Root or sudo access to your Linux server",
        "💻 Existing SSH access to your server (terminal/console)",
        "🔥 Basic understanding of firewall rules",
        "📝 A text editor (nano or vi)",
        "📦 Backup of your SSH configuration file",
        "🎯 A new port number (avoid ports below 1024 unless required)",
        "🧱 Permission to update firewall rules",
      ],
    },
    {
      heading: "Step-by-Step Guide: How to Change SSH Port 🚀",
      headingLevel: "h2",
      paragraphs: [
        "Follow these steps carefully to safely change your SSH port:",
      ],
    },
    {
      heading: "Step 1: Choose a New SSH Port 🎯",
      headingLevel: "h3",
      paragraphs: [
        "Select a port number between 1025 and 65535. Avoid commonly used ports:",
      ],
      list: [
        "Good choices: 2222, 22022, 7822, 10022, 22222",
        "Avoid: 22, 25 (SMTP), 53 (DNS), 80 (HTTP), 443 (HTTPS), 3306 (MySQL), 5432 (PostgreSQL)",
        "Best practice: Use ports above 10000 to avoid conflicts",
        "Example: We'll use port 2222 in this guide",
      ],
    },
    {
      heading: "Step 2: Backup SSH Configuration 💾",
      headingLevel: "h3",
      paragraphs: [
        "Always backup your SSH configuration before making changes:",
      ],
      list: [
        "Command: sudo cp /etc/ssh/sshd_config /etc/ssh/sshd_config.backup",
        "This creates a backup of the original SSH configuration file",
        "If something goes wrong, you can restore from the backup",
        "Verify backup: ls -la /etc/ssh/sshd_config*",
      ],
    },
    {
      heading: "Step 3: Edit SSH Configuration File ✍️",
      headingLevel: "h3",
      paragraphs: [
        "Open the SSH configuration file with a text editor:",
      ],
      list: [
        "Command: sudo nano /etc/ssh/sshd_config",
        "Alternative: sudo vi /etc/ssh/sshd_config",
        "Look for the line: #Port 22 (usually at the top)",
        "Remove the # symbol to uncomment the line",
        "Change 22 to your new port, e.g., Port 2222",
        "Save and exit (nano: Ctrl+X, then Y, then Enter)",
      ],
    },
    {
      heading: "Step 4: Verify Configuration Syntax ✅",
      headingLevel: "h3",
      paragraphs: [
        "Check if your SSH configuration file has correct syntax:",
      ],
      list: [
        "Command: sudo sshd -t",
        "Expected output: No output means configuration is correct",
        "If there are errors, you'll see detailed error messages",
        "This step prevents you from locking yourself out of the server",
      ],
    },
    {
      heading: "Step 5: Update Firewall Rules 🔥",
      headingLevel: "h3",
      paragraphs: [
        "Configure your firewall to allow traffic on the new SSH port:",
      ],
      list: [
        "For UFW (Ubuntu): sudo ufw allow 2222/tcp",
        "For UFW (Ubuntu): sudo ufw delete allow 22/tcp (remove old port)",
        "For firewalld (CentOS/RHEL): sudo firewall-cmd --add-port=2222/tcp --permanent",
        "For firewalld: sudo firewall-cmd --remove-port=22/tcp --permanent",
        "For firewalld: sudo firewall-cmd --reload",
        "Verify rules: sudo ufw status or sudo firewall-cmd --list-ports",
      ],
    },
    {
      heading: "Step 6: Restart SSH Service 🔄",
      headingLevel: "h3",
      paragraphs: [
        "Restart the SSH service to apply changes:",
      ],
      list: [
        "Command: sudo systemctl restart sshd",
        "Alternative: sudo service sshd restart",
        "Verify status: sudo systemctl status sshd",
        "Expected: Active (running) status",
      ],
    },
    {
      heading: "Step 7: Test New SSH Connection 🧪",
      headingLevel: "h3",
      paragraphs: [
        "Test the new SSH port before closing your current session. Open a new terminal/session (don't close the current one):",
      ],
      list: [
        "Command: ssh -p 2222 username@your_server_ip",
        "Replace 2222 with your chosen port",
        "Replace username with your actual username",
        "Replace your_server_ip with your server's IP address",
        "If successful, close the original connection and test from a fresh terminal",
      ],
    },
    {
      heading: "Distribution-Specific Instructions 🐧",
      headingLevel: "h2",
      paragraphs: [
        "Here are specific instructions for different Linux distributions:",
      ],
    },
    {
      heading: "Ubuntu/Debian - Change SSH Port",
      headingLevel: "h3",
      paragraphs: [
        "Complete steps for Ubuntu and Debian systems:",
      ],
      list: [
        "1. sudo cp /etc/ssh/sshd_config /etc/ssh/sshd_config.backup",
        "2. sudo nano /etc/ssh/sshd_config",
        "3. Find 'Port 22' and change to 'Port 2222'",
        "4. sudo sshd -t (check syntax)",
        "5. sudo systemctl restart sshd",
        "6. sudo ufw allow 2222/tcp",
        "7. sudo ufw delete allow 22/tcp",
        "8. Test: ssh -p 2222 user@ip",
      ],
    },
    {
      heading: "CentOS/RHEL - Change SSH Port",
      headingLevel: "h3",
      paragraphs: [
        "Complete steps for CentOS and RHEL systems:",
      ],
      list: [
        "1. sudo cp /etc/ssh/sshd_config /etc/ssh/sshd_config.backup",
        "2. sudo sed -i 's/#Port 22/Port 2222/' /etc/ssh/sshd_config",
        "3. sudo sshd -t (check syntax)",
        "4. sudo firewall-cmd --add-port=2222/tcp --permanent",
        "5. sudo firewall-cmd --remove-port=22/tcp --permanent",
        "6. sudo firewall-cmd --reload",
        "7. sudo systemctl restart sshd",
        "8. Test: ssh -p 2222 user@ip",
      ],
    },
    {
      heading: "Fedora - Change SSH Port",
      headingLevel: "h3",
      paragraphs: [
        "Steps for Fedora systems:",
      ],
      list: [
        "1. sudo cp /etc/ssh/sshd_config /etc/ssh/sshd_config.backup",
        "2. sudo nano /etc/ssh/sshd_config",
        "3. Change Port 22 to Port 2222",
        "4. sudo sshd -t",
        "5. sudo firewall-cmd --add-port=2222/tcp --permanent",
        "6. sudo firewall-cmd --remove-port=22/tcp --permanent",
        "7. sudo firewall-cmd --reload",
        "8. sudo systemctl restart sshd",
      ],
    },
    {
      heading: "Complete Configuration Example 📄",
      headingLevel: "h2",
      paragraphs: [
        "Here's how your SSH configuration should look after changes:",
      ],
      list: [
        "# Port to listen on",
        "Port 2222",
        "",
        "# Listen on all available interfaces",
        "#ListenAddress 0.0.0.0",
        "#ListenAddress ::",
        "",
        "# Protocol version",
        "Protocol 2",
        "",
        "# HostKey configuration",
        "HostKey /etc/ssh/ssh_host_rsa_key",
        "HostKey /etc/ssh/ssh_host_ecdsa_key",
      ],
    },
    {
      heading: "Security Best Practices 🔐",
      headingLevel: "h2",
      paragraphs: [
        "While changing SSH port helps, implement these additional security measures:",
      ],
      list: [
        "Use strong, unique passwords or SSH key-based authentication",
        "Disable password authentication and use SSH keys",
        "Disable root login: Set 'PermitRootLogin no' in sshd_config",
        "Limit login attempts using fail2ban or DenyHosts",
        "Use SSH key pairs instead of passwords",
        "Keep SSH software updated to latest version",
        "Monitor SSH logs for suspicious activity",
        "Use VPN or bastion hosts for additional security layer",
        "Implement IP whitelisting if possible",
        "Regularly audit SSH access logs",
      ],
    },
    {
      heading: "Connecting to SSH on a Custom Port 🔌",
      headingLevel: "h2",
      paragraphs: [
        "Once you've changed the SSH port, here's how to connect:",
      ],
      list: [
        "Command: ssh -p 2222 username@server_ip",
        "For SCP file transfer: scp -P 2222 file.txt user@server_ip:/path/",
        "For SSH config file (~/.ssh/config):",
        "  Host my-server",
        "    HostName server_ip",
        "    Port 2222",
        "    User username",
        "Then use: ssh my-server",
      ],
    },
    {
      heading: "SSH Config File Setup ⚙️",
      headingLevel: "h3",
      paragraphs: [
        "Create a local SSH config for easier management:",
      ],
      list: [
        "Edit: nano ~/.ssh/config",
        "Add the following for your server:",
        "Host prod-server",
        "  HostName 192.168.1.100",
        "  Port 2222",
        "  User admin",
        "  IdentityFile ~/.ssh/private_key",
        "Save and connect with: ssh prod-server",
      ],
    },
    {
      heading: "Troubleshooting Guide 🛠️",
      headingLevel: "h2",
      paragraphs: [
        "If you encounter issues after changing SSH port, follow these steps:",
      ],
    },
    {
      heading: "Can't Connect After Port Change? ❌",
      headingLevel: "h3",
      paragraphs: [
        "If you can't connect to SSH after changing the port:",
      ],
      list: [
        "Verify the new port is correct in sshd_config",
        "Check firewall allows the new port: sudo ufw status",
        "Ensure sshd service is running: sudo systemctl status sshd",
        "Check SSH error logs: sudo tail -f /var/log/auth.log",
        "Verify syntax again: sudo sshd -t",
        "Use netstat to verify port is listening: sudo netstat -tlnp | grep sshd",
        "Restore from backup if needed: sudo cp /etc/ssh/sshd_config.backup /etc/ssh/sshd_config",
      ],
    },
    {
      heading: "SSH Service Won't Start ⚠️",
      headingLevel: "h3",
      paragraphs: [
        "If SSH service fails to start after configuration changes:",
      ],
      list: [
        "Check syntax errors: sudo sshd -t",
        "Review the error message carefully",
        "Restore backup: sudo cp /etc/ssh/sshd_config.backup /etc/ssh/sshd_config",
        "Try restarting: sudo systemctl restart sshd",
        "Check logs: sudo journalctl -xe",
      ],
    },
    {
      heading: "Port Already in Use 🚫",
      headingLevel: "h3",
      paragraphs: [
        "If your chosen port is already in use:",
      ],
      list: [
        "Check what's using the port: sudo lsof -i :2222",
        "Change to a different port number",
        "Verify new port is free: sudo netstat -tlnp | grep :NEW_PORT",
      ],
    },
    {
      heading: "Firewall Rules Not Working 🧱",
      headingLevel: "h3",
      paragraphs: [
        "If firewall changes don't take effect:",
      ],
      list: [
        "Verify firewall is enabled: sudo ufw status",
        "Add rule with sudo: sudo ufw allow 2222/tcp",
        "For firewalld: sudo firewall-cmd --reload",
        "Check active rules: sudo ufw show added",
      ],
    },
    {
      heading: "Advanced SSH Port Security 🔒",
      headingLevel: "h2",
      paragraphs: [
        "Take SSH security to the next level with these advanced configurations:",
      ],
      list: [
        "Use fail2ban to block multiple failed login attempts",
        "Implement port knocking for an additional security layer",
        "Use SSH certificates instead of key pairs",
        "Set up SSH with two-factor authentication",
        "Use chroot to restrict SFTP users",
        "Implement rate limiting on SSH connections",
        "Configure SSH to run on multiple ports for redundancy",
      ],
    },
    {
      heading: "Port Forwarding and SSH Tunneling 🌐",
      headingLevel: "h2",
      paragraphs: [
        "Use SSH tunneling with your new custom port:",
      ],
      list: [
        "Local port forwarding: ssh -p 2222 -L 8080:localhost:80 user@server",
        "Remote port forwarding: ssh -p 2222 -R 8080:localhost:80 user@server",
        "Dynamic SOCKS proxy: ssh -p 2222 -D 1080 user@server",
        "Useful for accessing databases and internal services securely",
      ],
    },
    {
      heading: "Common SSH Port Configuration Mistakes ⚠️",
      headingLevel: "h2",
      paragraphs: [
        "Avoid these common errors when changing SSH port:",
      ],
      list: [
        "Don't use ports below 1024 without root access",
        "Don't forget to update firewall rules",
        "Don't change port in sshd_config without testing first",
        "Don't close current SSH session before testing new port",
        "Don't forget to restart sshd service after changes",
        "Don't ignore syntax checking with sshd -t command",
        "Don't use reserved or commonly used port numbers",
      ],
    },
    {
      heading: "SSH Port Change Best Practices ✅",
      headingLevel: "h2",
      paragraphs: [
        "Follow these best practices for successful SSH port changes:",
      ],
      list: [
        "Always backup original configuration before making changes",
        "Test new port connection before closing current session",
        "Document your port changes and keep them secure",
        "Combine port change with other security measures",
        "Update SSH client configurations across your team",
        "Monitor SSH logs after making changes",
        "Consider using SSH keys instead of passwords",
        "Regularly review SSH access logs and configurations",
      ],
    },
    {
      heading: "Useful SSH Port Commands Cheatsheet 📌",
      headingLevel: "h2",
      paragraphs: [
        "Quick reference for common SSH port management commands:",
      ],
      list: [
        "Check current SSH port: grep '^Port' /etc/ssh/sshd_config",
        "Test SSH config: sudo sshd -t",
        "Restart SSH service: sudo systemctl restart sshd",
        "Check SSH status: sudo systemctl status sshd",
        "View SSH logs: sudo tail -f /var/log/auth.log",
        "List listening ports: sudo netstat -tlnp",
        "Check specific port: sudo lsof -i :2222",
        "UFW allow port: sudo ufw allow 2222/tcp",
        "Firewalld allow port: sudo firewall-cmd --add-port=2222/tcp --permanent",
      ],
    },
    {
      heading: "Reverting SSH Port Changes",
      headingLevel: "h2",
      paragraphs: [
        "If you need to revert back to the default SSH port:",
      ],
      list: [
        "1. Restore from backup: sudo cp /etc/ssh/sshd_config.backup /etc/ssh/sshd_config",
        "2. Verify syntax: sudo sshd -t",
        "3. Update firewall to allow port 22 again",
        "4. Restart SSH: sudo systemctl restart sshd",
        "5. Test connection on port 22",
      ],
    },
    {
      heading: "Conclusion",
      headingLevel: "h2",
      paragraphs: [
        "Changing your SSH port is a simple yet effective security measure that can significantly reduce the number of unauthorized access attempts on your server. By following this comprehensive guide, you can safely change SSH port on Ubuntu, CentOS, Debian, and other Linux distributions.",
        "Remember, changing SSH port is just one part of a comprehensive server security strategy. Combine it with SSH key authentication, firewall rules, fail2ban, and regular security updates for maximum protection.",
        "If you encounter any issues during the process, refer to our troubleshooting section or restore from the backup we created at the beginning. Always test before closing your current SSH session to avoid getting locked out of your server.",
      ],
    },
    {
      heading: "Frequently Asked Questions",
      headingLevel: "h2",
      paragraphs: [
        "Q: Is changing SSH port really necessary for security?",
        "A: While it's not foolproof, changing SSH port significantly reduces automated attacks. Combined with other measures like SSH keys and fail2ban, it's a worthwhile step.",
        "",
        "Q: What's the best SSH port to use?",
        "A: Any port above 1024 and not used by other services works. Popular choices are 2222, 7822, 10022. Avoid standard service ports.",
        "",
        "Q: Will changing SSH port break my connection?",
        "A: Only if you don't update firewall rules or test properly. Always test the new port before closing the current session.",
        "",
        "Q: Can I run SSH on multiple ports?",
        "A: Yes, you can add multiple Port lines in sshd_config to listen on multiple ports simultaneously.",
        "",
        "Q: How do I know if my chosen port is available?",
        "A: Use: sudo netstat -tlnp | grep :PORT_NUMBER to check if the port is in use.",
        "",
        "Q: Should I disable SSH port 22 completely?",
        "A: Yes, after confirming the new port works. Remove or comment out the old port and restart SSH.",
        "",
        "Q: What if I forget my new SSH port?",
        "A: Check the sshd_config file using console or physical access: grep Port /etc/ssh/sshd_config",
        "",
        "Q: Is SSH port change compatible with cloud providers?",
        "A: Yes, it works with AWS, DigitalOcean, Azure, and other providers. Just ensure firewall/security group rules are updated.",
      ],
    },
  ],
  internalLinks: [
    {
      text: "Why Change Your SSH Port",
      section: "Why Change Your SSH Port?",
    },
    {
      text: "Prerequisites",
      section: "Prerequisites",
    },
    {
      text: "Step-by-Step Guide",
      section: "Step-by-Step Guide: How to Change SSH Port",
    },
    {
      text: "Ubuntu/Debian Instructions",
      section: "Ubuntu/Debian - Change SSH Port",
    },
    {
      text: "CentOS/RHEL Instructions",
      section: "CentOS/RHEL - Change SSH Port",
    },
    {
      text: "Security Best Practices",
      section: "Security Best Practices",
    },
    {
      text: "SSH Config File Setup",
      section: "SSH Config File Setup",
    },
    {
      text: "Troubleshooting Guide",
      section: "Troubleshooting Guide",
    },
    {
      text: "Commands Cheatsheet",
      section: "Useful SSH Port Commands Cheatsheet",
    },
  ],
};

export default changeSshPortPost;
